<template>
  <div class="digital-lecture-speaker-detail-page">
    <div class="container">
      <BaseBreadcrumbs :items="breadcrumbs" />

      <div v-if="speaker" class="wrapper-grid">
        <DigitalLectureSpeakerDetailHeader :speaker="speaker" />

        <DigitalLectureSpeakerDetailInfo
          v-if="speaker.description"
          :description="speaker.description"
        />

        <DigitalLectureSpeakerDetailList v-if="speaker.videos.length">
          <DigitalLectureVideoCard
            v-for="video in speaker.videos"
            :key="video.id"
            :video="video"
          />
        </DigitalLectureSpeakerDetailList>

        <DigitalLectureSpeakerDetailList
          v-if="speaker.research_showcases?.length"
          title="Проекты спикера"
          class="research-showcase-list"
        >
          <ResearchShowcaseCard
            v-for="card in speaker.research_showcases"
            :key="card.id"
            :card="card"
          />
        </DigitalLectureSpeakerDetailList>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseBreadcrumbs } from '~/components/base-ui/breadcrumbs'
  import {
    DigitalLectureSpeakerDetailHeader,
    DigitalLectureSpeakerDetailInfo,
    DigitalLectureSpeakerDetailList,
  } from '~/components/digital-lecture/digital-lecture-speaker'
  import { DigitalLectureVideoCard } from '~/components/digital-lecture/digital-lecture-main'
  import { ResearchShowcaseCard } from '~/components/research-showcase/research-showcase-main'
  import type { DigitalLectureSpeakerDetail } from '~/models/digital-lecture'

  const route = useRoute()
  const { $services } = useNuxtApp()

  const id = Number(route.params.id)
  if (!id || Number.isNaN(id)) {
    throw createError({ statusCode: 404, message: 'Страница не найдена' })
  }

  const { data: speaker, error } = await useAsyncData<DigitalLectureSpeakerDetail | null>(
    `digital-lecture-speaker-${id}`,
    () => $services.digitalLectureService.main.getSpeakerById(id),
  )

  if (error.value || !speaker.value?.id) {
    throw createError({ statusCode: 404, message: 'Страница не найдена' })
  }

  const { breadcrumbs } = await useBreadcrumbs()

  const fullName = computed(() =>
    speaker.value
      ? [speaker.value.name, speaker.value.middle_name, speaker.value.last_name]
          .filter(Boolean)
          .join(' ')
      : '',
  )

  useSeoMeta({
    title: fullName,
    description: speaker.value?.position || undefined,
    ogTitle: fullName,
    ogImage: speaker.value?.photo || undefined,
  })
</script>

<style lang="scss" scoped>
  .digital-lecture-speaker-detail-page {
    display: flex;
    justify-content: center;
    padding: 40px 0;

    > .container {
      @extend %flex-column;

      gap: 56px;
      max-width: 1440px;
      width: 100%;
      padding: 0 80px;

      @include vp-1279 {
        padding: 0 40px;
        gap: 24px;
      }

      @include vp-767 {
        padding: 0 16px;
      }
    }

    > .container > .wrapper-grid {
      @extend %flex-column;

      gap: 32px;

      @include vp-1279 {
        gap: 24px;
      }
    }
  }

  .research-showcase-list {
    min-width: 0;

    :deep(.research-showcase-card) {
      background-color: $color-default-white;
      min-width: 0;
    }

    :deep(.list) {
      grid-template-columns: repeat(3, minmax(0, 1fr));

      @include vp-1279 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include vp-1023 {
        grid-template-columns: minmax(0, 1fr);
      }
    }
  }
</style>
